import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import dayjs from 'dayjs';

import CurrencyAmount from 'components/currency/CurrencyAmount';
import { paymentStatusChipSx } from 'components/table/tableChipStyles';
import { getOrderPaymentDetail } from 'services/payment.service';
import { useToast } from 'contexts/ToastContext';
import { brand } from 'utils/designTokens';
import RefundTransactionDialog from 'pages/payment/RefundTransactionDialog';

export default function OrderPaymentDialog({ open, order, onClose, onRefreshOrder }) {
  const { showApiError } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refundDialog, setRefundDialog] = useState(false);

  const loadPaymentDetail = useCallback(
    async (orderId) => {
      setLoading(true);
      try {
        const res = await getOrderPaymentDetail(orderId);
        if (res?.data) {
          setData(res.data);
        }
      } catch (err) {
        showApiError(err, 'Failed to load order payment details.');
      } finally {
        setLoading(false);
      }
    },
    [showApiError]
  );

  useEffect(() => {
    if (open && order?.id) {
      loadPaymentDetail(order.id);
    } else {
      setData(null);
    }
  }, [open, order?.id, loadPaymentDetail]);

  const isRefundable = Boolean(data?.financials?.is_refundable);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PaymentsRoundedIcon color="primary" />
              <Typography variant="h5" fontWeight={700}>
                Order Payment &amp; Refund
              </Typography>
            </Stack>
            {data?.order?.payment_status && (
              <Chip
                label={data.order.payment_status}
                size="small"
                sx={paymentStatusChipSx(data.order.payment_status)}
              />
            )}
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2.5, pb: 3 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={6}>
              <CircularProgress size={32} />
            </Box>
          ) : !data ? (
            <Typography color="text.secondary" align="center" py={4}>
              No payment records found for this order.
            </Typography>
          ) : (
            <Stack spacing={2.5}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: brand.canvas,
                  border: `1px solid ${brand.border}`
                }}
              >
                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                      Grand Total:
                    </Typography>
                    <CurrencyAmount value={data.financials?.grand_total} variant="h6" fontWeight={700} />
                  </Grid>

                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                      Paid Amount:
                    </Typography>
                    <CurrencyAmount
                      value={data.financials?.paid_amount}
                      variant="h6"
                      fontWeight={700}
                      color="primary.main"
                    />
                  </Grid>

                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                      Refunded:
                    </Typography>
                    <CurrencyAmount value={data.financials?.refunded_amount || 0} variant="body1" fontWeight={600} />
                  </Grid>

                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                      Refundable Balance:
                    </Typography>
                    <CurrencyAmount
                      value={data.financials?.remaining_refundable_amount || 0}
                      variant="body1"
                      fontWeight={700}
                      color="#2e7d32"
                    />
                  </Grid>
                </Grid>
              </Box>

              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Order Number:
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {data.order?.order_no || `#${data.order?.id}`}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Payment Method:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {data.payment_method?.name || (data.transaction?.gateway === 'stripe' ? 'Card / Stripe' : 'Cash')}
                  </Typography>
                </Stack>

                {data.transaction?.stripe_payment_intent_id && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Payment Intent:
                    </Typography>
                    <Typography variant="caption" fontFamily="monospace">
                      {data.transaction.stripe_payment_intent_id}
                    </Typography>
                  </Stack>
                )}

                {data.transaction?.stripe_charge_id && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Stripe Charge:
                    </Typography>
                    <Typography variant="caption" fontFamily="monospace">
                      {data.transaction.stripe_charge_id}
                    </Typography>
                  </Stack>
                )}

                {data.transaction?.paid_at && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Paid At:
                    </Typography>
                    <Typography variant="caption">
                      {dayjs(data.transaction.paid_at).format('YYYY-MM-DD HH:mm')}
                    </Typography>
                  </Stack>
                )}

                {/* Cash on delivery notice */}
                {!data.transaction && (
                  <Box sx={{ mt: 1, p: 1.5, borderRadius: 1.5, bgcolor: 'action.hover' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      <strong>Cash on Delivery:</strong> No card or online payment gateway was processed for this order.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                      {Number(data.financials?.paid_amount || 0) >= Number(data.financials?.grand_total || 0) ? (
                        'Cash collection has been recorded and reconciled by administration.'
                      ) : (
                        <Stack direction="row" spacing={0.3} alignItems="center" component="span" flexWrap="wrap">
                          <span>Outstanding cash collection due on delivery:</span>
                          <CurrencyAmount
                            value={Math.max(0, Number(data.financials?.grand_total || 0) - Number(data.financials?.paid_amount || 0))}
                            variant="caption"
                            color="text.secondary"
                            decimals={2}
                          />
                          <span>.</span>
                        </Stack>
                      )}
                    </Typography>
                  </Box>
                )}
              </Stack>

              {/* Refund Audit History */}
              {Array.isArray(data.refund_history) && data.refund_history.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} mb={1}>
                    Refund History ({data.refund_history.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: brand.canvas }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">
                            Amount
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Note</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.refund_history.map((h, i) => (
                          <TableRow key={h.id || i}>
                            <TableCell>
                              <Chip
                                label={h.payment_status}
                                size="small"
                                sx={paymentStatusChipSx(h.payment_status)}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <CurrencyAmount value={h.amount} fontWeight={600} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {h.note || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {h.timestamp ? dayjs(h.timestamp).format('YYYY-MM-DD HH:mm') : '—'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.5, justifyContent: 'space-between' }}>
          <Button onClick={onClose} color="inherit">
            Close
          </Button>

          {isRefundable && data && (
            <Button
              variant="contained"
              startIcon={<CurrencyExchangeIcon />}
              onClick={() => setRefundDialog(true)}
              sx={{
                bgcolor: brand.primary,
                '&:hover': { bgcolor: brand.primaryDark }
              }}
            >
              Issue Refund
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Direct Stripe Refund Dialog */}
      {refundDialog && data && (
        <RefundTransactionDialog
          open={refundDialog}
          item={data}
          onClose={() => setRefundDialog(false)}
          onSuccess={() => {
            loadPaymentDetail(order.id);
            if (onRefreshOrder) onRefreshOrder();
          }}
        />
      )}
    </>
  );
}

OrderPaymentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  order: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onRefreshOrder: PropTypes.func
};
